import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTicketStore } from '@stores/ticketStore';
import { useAnnouncementStore } from '@stores/announcementStore';
import type { TicketNotification, Announcement } from '@typings/ticket';
import { NOTIFICATION_TYPE_MAP, ANNOUNCEMENT_TYPE_MAP } from '@typings/ticket';

export const NotificationBell: React.FC = () => {
  const navigate = useNavigate();
  const {
    unreadCount,
    notifications,
    fetchUnreadCount,
    fetchNotifications,
    markNotificationRead,
    markAllNotificationsRead,
  } = useTicketStore();
  const {
    unreadAnnouncementCount,
    announcements,
    fetchAnnouncementUnreadCount,
    fetchAnnouncements,
    markAnnouncementRead,
    markAllAnnouncementsRead,
  } = useAnnouncementStore();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const totalUnread = unreadCount + unreadAnnouncementCount;

  useEffect(() => {
    fetchUnreadCount();
    fetchAnnouncementUnreadCount();
    const interval = setInterval(() => {
      fetchUnreadCount();
      fetchAnnouncementUnreadCount();
    }, 60000);
    return () => clearInterval(interval);
  }, [fetchUnreadCount, fetchAnnouncementUnreadCount]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleOpen = () => {
    if (!open) {
      fetchNotifications(1);
      fetchAnnouncements(1);
    }
    setOpen(!open);
  };

  const handleNotificationClick = async (notification: TicketNotification) => {
    if (!notification.is_read) {
      await markNotificationRead(notification.id);
    }
    setOpen(false);
    navigate(`/tickets/${notification.ticket}`);
  };

  const handleAnnouncementClick = async (announcement: Announcement) => {
    if (!announcement.is_read) {
      await markAnnouncementRead(announcement.id);
    }
    setOpen(false);
    navigate('/notifications');
  };

  const handleMarkAllRead = async () => {
    await Promise.all([
      markAllNotificationsRead(),
      markAllAnnouncementsRead(),
    ]);
  };

  const unreadNotifications = notifications.filter(n => !n.is_read).slice(0, 5);
  const unreadAnnouncements = announcements.filter(a => !a.is_read).slice(0, 3);

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={handleOpen}
        className="relative p-2 text-gray-500 hover:text-gray-700 transition-colors"
        title="通知中心"
      >
        <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
        </svg>
        {totalUnread > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 bg-red-500 text-white text-[10px] font-bold rounded-full flex items-center justify-center min-w-[18px] h-[18px]">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-lg shadow-lg border border-gray-200 z-50 max-h-96 flex flex-col">
          <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
            <h3 className="text-sm font-medium text-gray-900">通知中心</h3>
            {totalUnread > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-blue-600 hover:text-blue-700"
              >
                全部已读
              </button>
            )}
          </div>
          <div className="overflow-y-auto flex-1">
            {unreadAnnouncements.length === 0 && unreadNotifications.length === 0 ? (
              <p className="text-sm text-gray-500 text-center py-6">暂无通知</p>
            ) : (
              <>
                {/* Announcements section */}
                {unreadAnnouncements.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 bg-gray-50 text-xs font-medium text-gray-500 sticky top-0">
                      公告通知
                    </div>
                    {unreadAnnouncements.map(ann => (
                      <div
                        key={ann.id}
                        onClick={() => handleAnnouncementClick(ann)}
                        className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-50 bg-orange-50"
                      >
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-orange-500 rounded-full mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 truncate">{ann.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {ANNOUNCEMENT_TYPE_MAP[ann.type]} · {new Date(ann.published_at).toLocaleString('zh-CN')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}

                {/* Ticket notifications section */}
                {unreadNotifications.length > 0 && (
                  <>
                    <div className="px-4 py-1.5 bg-gray-50 text-xs font-medium text-gray-500 sticky top-0">
                      工单通知
                    </div>
                    {unreadNotifications.map(notif => (
                      <div
                        key={notif.id}
                        onClick={() => handleNotificationClick(notif)}
                        className="px-4 py-3 cursor-pointer hover:bg-gray-50 border-b border-gray-50 bg-blue-50"
                      >
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 bg-blue-500 rounded-full mt-1.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-gray-800 truncate">{notif.title}</p>
                            <p className="text-xs text-gray-400 mt-0.5">
                              {NOTIFICATION_TYPE_MAP[notif.type] || notif.type} · {new Date(notif.created_at).toLocaleString('zh-CN')}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </>
            )}
          </div>
          <div className="px-4 py-2 border-t border-gray-100">
            <button
              onClick={() => { setOpen(false); navigate('/notifications'); }}
              className="text-xs text-blue-600 hover:text-blue-700 w-full text-center"
            >
              查看全部通知
            </button>
          </div>
        </div>
      )}
    </div>
  );
};
